<template>
  <div class="kd">
    <section class="card overview">
      <div class="ov-left">
        <span class="crown" aria-hidden="true">♔</span>
        <div>
          <div class="ov-title">
            <b>{{ displayName }}</b>
            <button type="button" class="pill" @click="toast('当前为同名王国详情')">{{ model.kind }}</button>
          </div>
          <div class="ov-king">当前龙头：{{ leaderLine }}</div>
        </div>
      </div>
      <div class="ov-metrics">
        <div v-for="item in overviewMetrics" :key="item.label" class="ov-metric">
          <span>{{ item.label }}</span>
          <b :class="item.tone">{{ item.value }}</b>
          <em v-if="item.hint">{{ item.hint }}</em>
        </div>
        <button type="button" class="primary ov-save" @click="saveConceptAttrs">保存</button>
        <button type="button" class="ghost ov-clear" @click="clearConceptAttrs">清空概念</button>
      </div>
      <div v-if="heightBar" class="ov-height">
        <div class="ov-h-metric">
          <span>非龙头最高高度</span>
          <b>{{ heightBar.height || '—' }}</b>
        </div>
        <div
          v-for="(name, i) in heightBar.attrs"
          :key="'ha-' + i"
          class="ov-attr"
          :class="{ filled: !!name, next: heightBar.pickMode && heightBar.nextIndex === i }"
        >
          <span>主属性{{ i + 1 }}</span>
          <b v-if="name">{{ name }}</b>
          <button type="button" @click="onAttrAdd">{{ heightBar.pickMode && heightBar.nextIndex === i ? '点选中' : '添加' }}</button>
          <button v-if="name" type="button" class="x" @click="onAttrClear(i)">×</button>
        </div>
      </div>
      <div class="ov-actions">
        <div class="important-enter">
          <button type="button" class="ghost" @click.stop="importantMenu = !importantMenu">进入重要王国</button>
          <div v-if="importantMenu" class="important-menu">
            <button
              v-for="n in [1, 2, 3]"
              :key="'enter-imp-' + n"
              type="button"
              @click="chooseImportant(n - 1)"
            >
              重要王国{{ n }}号
              <em>{{ importantSlotHint(n - 1) }}</em>
            </button>
          </div>
        </div>
        <button type="button" class="ghost" @click="openRename">王国重命名</button>
        <div class="war-stack">
          <button type="button" class="ghost" @click="exitWar">退出战争</button>
          <button v-if="importantView" type="button" class="ghost leave-important" @click="$emit('leave-important')">退出重要王国</button>
        </div>
      </div>
    </section>

    <section class="roles">
      <article
        v-for="role in roleCards"
        :key="role.key"
        class="role-shell"
        :class="['tone-' + role.tone, { picking: rolePick === role.key, 'height-pick': canHeightPickRole(role), 'swap-target': !!rolePick && rolePick !== role.key }]"
        @click="onRoleCardClick(role)"
      >
        <header class="role-head">
          <span>{{ role.tag }}</span>
          <button
            type="button"
            class="role-pick"
            :class="{ on: rolePick === role.key }"
            @click.stop="toggleRolePick(role.key)"
          >{{ rolePick === role.key ? '点选中' : '选择' }}</button>
        </header>
        <div class="role-slots">
          <div
            v-for="row in visibleRoleSlots(role)"
            :key="role.key + '-' + row.i"
            class="role-slot"
            :class="{ empty: !row.slot.code, next: rolePick === role.key && nextRoleSlotIndex(role) === row.i }"
            @click.stop="onRoleSlotClick(role, row.i)"
          >
            <button
              v-if="row.slot.code && role.key !== 'leader'"
              type="button"
              class="slot-x"
              title="移出这个位置"
              @click.stop="clearRoleSlot(role.key, row.i)"
            >×</button>
            <LeaderColumn
              v-if="row.slot.leader"
              :leader="row.slot.leader"
              :rank="row.slot.rank"
              :show-catchup="false"
              :return-meta="returnMeta"
            />
            <div v-else-if="row.slot.code" class="role-compact">
              <div class="compact-top">
                <b>{{ row.slot.name || '—' }}</b>
                <div class="today">
                  <span>今日涨幅</span>
                  <em :class="pctTone(quoteOf(row.slot.code).changePct)">{{ pctText(quoteOf(row.slot.code).changePct) }}</em>
                </div>
              </div>
              <div v-if="slotTags(row.slot).length" class="compact-tags">
                <span v-for="tag in slotTags(row.slot)" :key="tag">{{ tag }}</span>
              </div>
              <div class="returns">
                <div class="ret"><span>10日总涨幅</span><b :class="pctTone(quoteOf(row.slot.code).d10)">{{ pctText(quoteOf(row.slot.code).d10) }}</b></div>
                <div class="ret"><span>15日总涨幅</span><b :class="pctTone(quoteOf(row.slot.code).d15)">{{ pctText(quoteOf(row.slot.code).d15) }}</b></div>
                <div class="ret"><span>20日总涨幅</span><b :class="pctTone(quoteOf(row.slot.code).d20)">{{ pctText(quoteOf(row.slot.code).d20) }}</b></div>
              </div>
            </div>
            <div v-else-if="role.key === 'leader' && loading" class="role-missing">正在读取监控池…</div>
            <div v-else class="role-missing">{{ role.key === 'leader' ? roleEmptyText(role) : '空位' }}</div>
          </div>
        </div>
      </article>
    </section>
    <p v-if="rolePick" class="role-pick-hint">
      点选已开：点击另一张总龙、趋势龙或连板龙可互换位置。点击下方连板池、题材10日涨幅、高度名单、接力池或老臣记录池中的股票，填入当前卡片。再点「点选中」可结束。
    </p>

    <ThemePropShell
      ref="themeShell"
      embed
      :focus-code="themeLeader.code"
      :focus-name="themeLeader.name"
      :leaders="leaders"
      :loading="loading"
      :return-meta="returnMeta"
      :concepts-by-code="conceptsByCode"
      :concept-items-by-code="conceptItemsByCode"
      :limit-up-rows="limitUpRows"
      :trade-date="tradeDate"
      :manual-attrs="manualAttrs"
      :role-pick="rolePick"
      :sector-key="sectorTabKey"
      :sector-concept="mainConceptSpec.name"
      :sector-source="mainConceptSpec.source"
      :kingdom-name="displayName"
      :sync-dragons="syncDragons"
      :dragon-cards="dragonCards"
      :saved-main-attrs="savedMainAttrs"
      @ensure-concepts="$emit('ensure-concepts', $event)"
      @height-bar="onHeightBar"
      @role-stock="onRoleStock"
    >
      <section class="card block">
        <header class="block-h">
          <i class="mark" />
          <div>
            <b>{{ model.ladderTitle }}</b>
            <span>{{ model.ladderSub }}</span>
          </div>
        </header>
        <div class="ladder">
          <article v-for="col in ladders" :key="col.key" class="mini" :class="col.heat">
            <div class="mini-h">
              <b>{{ col.title }}</b>
              <em>{{ col.rows.length }} 只</em>
            </div>
            <table :class="{ 'role-picking': !!rolePick }">
              <thead>
                <tr>
                  <th>股票名称</th>
                  <th>代码</th>
                  <th>中期筹码分</th>
                  <th>全周期分</th>
                  <th>自流通值</th>
                  <th>综合分</th>
                  <th>涨停时间</th>
                  <th>匹配分</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in visibleLadderRows(col)"
                  :key="col.key + row.code"
                  :class="{ pickable: !!rolePick }"
                  @click="onLadderPick(row)"
                >
                  <td class="nm">{{ row.name }}</td>
                  <td>{{ row.code }}</td>
                  <td>{{ row.midChip }}</td>
                  <td>{{ row.fullChip }}</td>
                  <td>{{ row.floatCap }}</td>
                  <td>{{ row.totalScore }}</td>
                  <td>{{ row.limitTime }}</td>
                  <td>{{ row.matchScore }}</td>
                </tr>
              </tbody>
            </table>
            <button
              v-if="col.key === 'first' && col.rows.length > 4"
              type="button"
              class="fold"
              @click="ladderOpen = !ladderOpen"
            >{{ ladderOpen ? '收起' : ('展开其余 ' + (col.rows.length - 4) + ' 只') }}</button>
          </article>
        </div>
      </section>
    </ThemePropShell>

    <div v-if="renameOpen" class="mask" @click.self="renameOpen = false">
      <div class="dialog">
        <header>王国重命名</header>
        <input
          v-model="renameDraft"
          class="rename-input"
          maxlength="24"
          placeholder="王国名称"
          @keyup.enter="confirmRename"
        />
        <p class="note">只改王国名称。概念和龙头选择保持不变。</p>
        <div class="rename-actions">
          <button type="button" class="ghost" @click="renameOpen = false">取消</button>
          <button type="button" class="primary" @click="confirmRename">确认</button>
        </div>
      </div>
    </div>
    <div v-if="conceptOpen" class="mask" @click.self="conceptOpen = false">
      <div class="dialog wide">
        <header>概念详情</header>
        <div class="chips">
          <span v-for="chip in model.conceptChips" :key="'d-' + chip[0]" class="chip">{{ chip[0] }}<em>{{ chip[1] }}</em></span>
        </div>
        <p class="note">{{ kingdom ? '来自本次加入时选择的概念。' : '标签来自参考图，不是当日概念接口。' }}</p>
        <button type="button" class="primary" @click="conceptOpen = false">关闭</button>
      </div>
    </div>
  </div>
</template>

<script>
import LeaderColumn from '../LeaderColumn.vue'
import ThemePropShell from './ThemePropShell.vue'
import { CULTURE_KINGDOM_REFERENCE } from './kingdomDetailReference'
import { CONCEPT_SOURCE_MERGE } from '@/utils/conceptSource'
import { resolveAnalysisConcepts } from '@/utils/conceptFilter'
import stockApi from '@/api/stockApi'
import { formatSignedPct } from '@/services/leaderCommandCenterAdapter'

const ROLE_SLOTS = [
  { key: 'leader', tag: '龙头', tone: 'gold', code: '600825', name: '新华传媒' },
  { key: 'trend', tag: '趋势龙', tone: 'blue', code: '601811', name: '新华文轩' },
  { key: 'board', tag: '连板龙', tone: 'rose', code: '603096', name: '新经典' }
]

function clone(value) {
  return JSON.parse(JSON.stringify(value))
}

function conceptMetrics(main, subs) {
  const list = Array.isArray(subs) ? subs : []
  return [
    { label: '主概念', value: main || '—', hint: '必选', tone: 'blue' },
    { label: '副概念 1', value: list[0] || '—', hint: '可选', tone: '' },
    { label: '副概念 2', value: list[1] || '—', hint: '可选', tone: '' },
    { label: '副概念 3', value: list[2] || '—', hint: '可选', tone: '' }
  ]
}

function normCode6(code) {
  return String(code || '')
    .trim()
    .split('.')[0]
    .replace(/\D/g, '')
    .padStart(6, '0')
    .slice(-6)
}

function conceptNames(raw) {
  return (Array.isArray(raw) ? raw : []).map((item) => String(item || '').trim()).filter(Boolean)
}

function rowMatchesConcept(row, name, source) {
  const target = String(name || '').trim()
  if (!target) return false
  const wc = conceptNames(row && row.concepts)
  const kpl = conceptNames(row && row.kplConcepts)
  const local = conceptNames(row && row.localConcepts)
  if (source === 'KPL') return kpl.indexOf(target) >= 0
  if (source === 'WC') return wc.indexOf(target) >= 0
  if (source === '固定') return local.indexOf(target) >= 0
  return wc.indexOf(target) >= 0 || kpl.indexOf(target) >= 0 || local.indexOf(target) >= 0
}

function parseAttrSpec(raw) {
  const text = String(raw || '').trim()
  const matched = text.match(/[（(]\s*(KPL|WC|固定)\s*[）)]\s*$/i)
  const source = matched ? matched[1] : ''
  return {
    name: text.replace(/[（(]\s*(KPL|WC|MERGE|固定)\s*[）)]\s*$/i, '').trim(),
    source: source === '固定' ? '固定' : source.toUpperCase()
  }
}

function boardCount(row) {
  const n = Number(row && row.consecutiveBoards)
  if (Number.isFinite(n) && n > 0) return n
  const text = String((row && row.consecutiveBoardsLabel) || '')
  if (text.indexOf('首板') >= 0) return 1
  const matched = text.match(/(\d+)\s*板/)
  return matched ? Number(matched[1]) : 1
}

function chipPct(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return '—'
  return (n * 100).toFixed(2) + '%'
}

function oneDecimal(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return '—'
  return n.toFixed(1)
}

function yiText(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return '—'
  return n.toFixed(1) + '亿'
}

function nameCharScore(leaderName, stockName) {
  const a = String(leaderName || '').replace(/\s+/g, '')
  const b = String(stockName || '').replace(/\s+/g, '')
  if (!a || !b) return 0
  let score = 0
  for (let i = 0; i < b.length; i += 1) {
    if (a.indexOf(b[i]) >= 0) score += 1
  }
  return score
}

function matchWithLeader(row, leaderName, leaderConcepts, mainConcept) {
  const highlighted = String(mainConcept || '').trim()
  const leaderSet = new Set((leaderConcepts || []).map((item) => String(item || '').trim()).filter(Boolean))
  if (highlighted) leaderSet.add(highlighted)
  if (!leaderName && !leaderSet.size) return '—'
  const stockConcepts = resolveAnalysisConcepts(row && row.concepts, row && row.kplConcepts, CONCEPT_SOURCE_MERGE, row && row.localConcepts)
  let general = 0
  let picked = 0
  stockConcepts.forEach((name) => {
    if (!leaderSet.has(name)) return
    if (highlighted && name === highlighted) picked += 5
    else general += 1
  })
  return String(nameCharScore(leaderName, row && row.name) + general + picked)
}

function ladderLine(row, leaderName, leaderConcepts, mainConcept) {
  const code = normCode6(row && (row.code || row.ts_code))
  const boards = boardCount(row)
  const cap = row && (row.freeFloatMarketCap != null ? row.freeFloatMarketCap : row.marketValue)
  return {
    name: String((row && row.name) || ''),
    code: code && code !== '000000' ? code : '',
    midChip: chipPct(row && row.midChip),
    fullChip: chipPct(row && row.fullChip),
    floatCap: yiText(cap),
    totalScore: oneDecimal(row && row.preScreenScore),
    limitTime: String((row && row.limitUpTime) || '').trim() || '—',
    matchScore: matchWithLeader(row, leaderName, leaderConcepts, mainConcept),
    boards
  }
}

function limitTimeKey(text) {
  const matched = String(text || '').match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/)
  if (!matched) return 999999
  return Number(matched[1]) * 3600 + Number(matched[2]) * 60 + Number(matched[3] || 0)
}

function sortRows(list) {
  return list
    .slice()
    .sort((a, b) => limitTimeKey(a.limitTime) - limitTimeKey(b.limitTime) || String(a.name).localeCompare(String(b.name), 'zh'))
}

const ROLE_OVERRIDE_PREFIX = 'lcc.kingdom.roleOverride.v1|'

function readRoleOverrides(id) {
  try {
    const raw = localStorage.getItem(ROLE_OVERRIDE_PREFIX + id)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch (err) {
    return {}
  }
}

function readStoredSlots(ov) {
  if (!ov || typeof ov !== 'object') return []
  if (Array.isArray(ov.slots)) {
    return ov.slots
      .map((item) => (item && typeof item === 'object' ? item : null))
      .filter((item) => item && (item.code || item.cleared))
  }
  if (ov.cleared) return [{ cleared: true }]
  if (ov.code) return [{ code: ov.code, name: ov.name || '', boardLabel: ov.boardLabel || '' }]
  return []
}

function packRoleFields(leaderCode, members) {
  const trendCodes = []
  const boardCodes = []
  ;(members || []).forEach((item) => {
    const code = normCode6(item && item.code)
    if (!code || code === '000000') return
    if (item.role === 'trend') trendCodes.push(code)
    if (item.role === 'board') boardCodes.push(code)
  })
  return {
    leader: normCode6(leaderCode) || '',
    trend: trendCodes[0] || '',
    board: boardCodes[0] || '',
    trendCodes,
    boardCodes
  }
}

export default {
  name: 'KingdomDetailView',
  components: { LeaderColumn, ThemePropShell },
  props: {
    leaders: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    returnMeta: { type: Object, default: () => ({}) },
    pins: { type: Object, default: () => ({}) },
    kingdom: { type: Object, default: null },
    tradeDate: { type: String, default: '' },
    limitUpRows: { type: Array, default: () => [] },
    limitUpReady: { type: Boolean, default: false },
    conceptsByCode: { type: Object, default: () => ({}) },
    conceptItemsByCode: { type: Object, default: () => ({}) },
    manualAttrs: { type: Object, default: () => ({}) },
    cultureName: { type: String, default: '' },
    kingdomNames: { type: Array, default: () => [] },
    importantSlots: { type: Array, default: () => [] },
    importantView: { type: Boolean, default: false }
  },
  data() {
    const model = clone(CULTURE_KINGDOM_REFERENCE)
    return {
      model,
      ladders: model.ladders,
      heightRows: model.heightRows,
      relayRows: model.relayRows,
      bottomTab: 'theme',
      renameOpen: false,
      renameDraft: '',
      conceptOpen: false,
      ladderOpen: false,
      heightBar: null,
      rolePick: '',
      roleOverrides: {},
      roleQuotes: {},
      roleQuoteSeq: 0,
      importantMenu: false
    }
  },
  computed: {
    displayName() {
      if (!this.kingdom && this.cultureName) return this.cultureName
      return String((this.model && this.model.name) || '').replace(/（(?:KPL|WC)）|\((?:KPL|WC)\)/g, '')
    },
    roleCards() {
      const list = this.leaders || []
      const overrides = this.roleOverrides || {}
      return this.roleSlots.map((slot) => {
        if (slot.key === 'leader') {
          const resolved = this.resolveRoleSlot(null, slot, list)
          return { ...slot, slots: [resolved], ...resolved }
        }
        if (slot.key === 'trend' || slot.key === 'board') {
          const holders = this.roleHolders(slot.key, slot)
          const stored = readStoredSlots(overrides[slot.key])
          const seen = new Set(holders.map((item) => normCode6(item && item.code)))
          stored.forEach((item) => {
            const code = normCode6(item && item.code)
            if (!code || code === '000000' || seen.has(code) || item.cleared) return
            seen.add(code)
            holders.push({ code, name: item.name || '', boardLabel: item.boardLabel || '' })
          })
          const slots = (holders.length ? holders : [null]).map((item) => this.resolveRoleSlot(item, null, list))
          const first = slots.find((item) => item.code) || slots[0]
          return {
            ...slot,
            slots,
            code: first.code,
            name: first.name,
            leader: first.leader,
            rank: first.rank,
            manual: first.manual,
            boardLabel: first.boardLabel
          }
        }
        return {
          ...slot,
          slots: [],
          ...this.resolveRoleSlot(null, slot, list)
        }
      })
    },
    roleQuoteCodes() {
      const codes = []
      const cards = this.roleCards || []
      for (let i = 0; i < cards.length; i += 1) {
        const slots = (cards[i] && cards[i].slots) || []
        for (let j = 0; j < slots.length; j += 1) {
          const slot = slots[j]
          if (slot && slot.code && !slot.leader) codes.push(slot.code)
        }
      }
      return codes.join(',')
    },
    roleSlots() {
      if (!this.kingdom) return ROLE_SLOTS
      const roles = this.kingdom.roles || {}
      const members = this.kingdom.members || []
      const nameOf = (code) => {
        const hit = members.find((item) => normCode6(item && item.code) === normCode6(code))
        return (hit && hit.name) || ''
      }
      return [
        { key: 'leader', tag: '总龙', tone: 'gold', code: roles.leader || '', name: nameOf(roles.leader) },
        { key: 'trend', tag: '趋势龙', tone: 'blue', code: roles.trend || '', name: nameOf(roles.trend) },
        { key: 'board', tag: '连板龙', tone: 'rose', code: roles.board || '', name: nameOf(roles.board) }
      ]
    },
    leaderLine() {
      const hit = this.roleCards.find((role) => role.key === 'leader')
      if (hit && hit.leader) return `${hit.leader.name || hit.name} ${hit.leader.code || hit.code}`
      if (this.loading) return '…'
      if (!hit || !hit.code) return '尚未指定'
      return `${hit.name || ''} ${hit.code} 不在监控池`
    },
    mainConceptSpec() {
      if (this.kingdom) {
        const label = String(this.kingdom.mainConceptLabel || '')
        const matched = label.match(/（(KPL|WC)）/)
        return {
          name: String(this.kingdom.mainConceptName || '').trim(),
          source: matched ? matched[1] : ''
        }
      }
      return { name: '文化传媒', source: 'KPL' }
    },
    overviewMetrics() {
      const metrics = (this.model && this.model.metrics) || []
      const attrs = (this.heightBar && this.heightBar.attrs) || []
      const out = []
      metrics.forEach((item) => {
        if (!item) return
        if (item.label === '主概念') {
          const live = !!(this.heightBar && Array.isArray(this.heightBar.attrs))
          out.push({ ...item, value: live ? (attrs[0] || '—') : (item.value || '—'), hint: '主属性1' })
          out.push({ label: '主属性2', value: attrs[1] || '—', hint: '跟随添加', tone: 'blue' })
          out.push({ label: '主属性3', value: attrs[2] || '—', hint: '跟随添加', tone: 'blue' })
          return
        }
        out.push(item)
      })
      return out
    },
    savedMainAttrs() {
      const raw = this.kingdom && this.kingdom.mainAttrSlots
      return Array.isArray(raw) ? raw.slice(0, 3) : []
    },
    ladderAttrSpecs() {
      return [this.attrRaw(0), this.attrRaw(1)].map(parseAttrSpec).filter((item) => item.name)
    },
    leaderRole() {
      return (this.roleCards || []).find((role) => role.key === 'leader') || null
    },
    sectorTabKey() {
      if (this.kingdom && this.kingdom.id) return String(this.kingdom.id)
      return 'culture-kpl'
    },
    themeLeader() {
      const hit = this.leaderRole
      if (!hit) return { code: '', name: '' }
      return {
        code: (hit.leader && (hit.leader.code || hit.leader.leaderId)) || hit.code || '',
        name: (hit.leader && hit.leader.name) || hit.name || ''
      }
    },
    dragonCards() {
      const out = []
      const cards = this.roleCards || []
      const specs = [
        { key: 'leader', tag: '总龙' },
        { key: 'trend', tag: '趋势龙' },
        { key: 'board', tag: '连板龙' }
      ]
      specs.forEach((spec) => {
        const role = cards.find((item) => item && item.key === spec.key)
        const slots = (role && role.slots) || []
        slots.forEach((slot, i) => {
          const code = normCode6((slot && slot.leader && slot.leader.code) || (slot && slot.code))
          if (!code || code === '000000') return
          const name = (slot && slot.leader && slot.leader.name) || (slot && slot.name) || code
          out.push({ key: `${spec.key}-${i}`, role: spec.key, tag: spec.tag, code, name })
        })
      })
      return out
    },
    syncDragons() {
      const out = []
      const seen = new Set()
      const push = (code, name) => {
        const key = normCode6(code)
        if (!key || key === '000000' || seen.has(key)) return
        seen.add(key)
        out.push({ code: key, name: name || '' })
      }
      const cards = this.roleCards || []
      const leader = cards.find((role) => role && role.key === 'leader')
      if (leader) push((leader.leader && leader.leader.code) || leader.code, (leader.leader && leader.leader.name) || leader.name)
      const trend = cards.find((role) => role && role.key === 'trend')
      const trendSlots = (trend && trend.slots) || []
      for (let i = 0; i < trendSlots.length; i += 1) {
        const slot = trendSlots[i]
        if (slot && slot.code) push(slot.code, slot.name)
      }
      return out
    }
  },
  watch: {
    kingdom: {
      immediate: true,
      handler() {
        this.rolePick = ''
        this.loadRoleOverrides()
        this.releaseClearedRoles()
        this.applyModel()
      }
    },
    limitUpRows() {
      this.rebuildLadders()
    },
    limitUpReady() {
      this.rebuildLadders()
    },
    leaders() {
      this.rebuildLadders()
    },
    tradeDate() {
      this.rebuildLadders()
      this.loadRoleQuotes()
    },
    conceptsByCode: {
      deep: true,
      handler() {
        this.rebuildLadders()
      }
    },
    heightBar: {
      deep: true,
      handler() {
        this.rebuildLadders()
      }
    },
    roleQuoteCodes: {
      immediate: true,
      handler() {
        this.loadRoleQuotes()
      }
    }
  },
  methods: {
    roleHolders(key, fallback) {
      const kingdom = this.kingdom
      const seen = new Set()
      const out = []
      const push = (code, name) => {
        const norm = normCode6(code)
        if (!norm || norm === '000000' || seen.has(norm)) return
        seen.add(norm)
        out.push({ code: norm, name: name || '' })
      }
      const members = (kingdom && kingdom.members) || []
      members.forEach((item) => {
        if (item && item.role === key) push(item.code, item.name)
      })
      if (!out.length && kingdom && kingdom.roles) {
        const roles = kingdom.roles
        const list = Array.isArray(roles[`${key}Codes`]) ? roles[`${key}Codes`] : []
        list.forEach((code) => push(code, ''))
        if (!out.length && roles[key]) push(roles[key], fallback && fallback.name)
      }
      return out
    },
    importantSlotHint(index) {
      const id = this.kingdom && this.kingdom.id ? this.kingdom.id : 'culture-kpl'
      const held = String((this.importantSlots || [])[index] || '')
      if (held && held === id) return '移出'
      if (held) return '替换'
      return '空位'
    },
    chooseImportant(slot) {
      this.importantMenu = false
      this.$emit('pin-important', { slot })
    },
    openRename() {
      this.renameDraft = this.displayName
      this.renameOpen = true
    },
    confirmRename() {
      const next = String(this.renameDraft || '').replace(/\s+/g, '')
      if (!next) {
        this.$message.warning('请填写王国名称')
        return
      }
      if (next === this.displayName) {
        this.renameOpen = false
        return
      }
      if ((this.kingdomNames || []).indexOf(next) >= 0) {
        this.$message.warning('已有同名王国')
        return
      }
      this.$emit('rename-kingdom', next)
      this.renameOpen = false
    },
    exitWar() {
      const name = (this.kingdom && this.kingdom.name) || this.displayName || '该板块'
      this.$confirm(`删除「${name}」板块卡？`, '退出战争', {
        confirmButtonText: '删除',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.$emit('exit-war')
      }).catch(() => {})
    },
    applyModel() {
      if (!this.kingdom) {
        const model = clone(CULTURE_KINGDOM_REFERENCE)
        model.metrics = conceptMetrics(model.name, [])
        this.model = model
        this.heightRows = (model.heightRows || []).slice()
        this.relayRows = (model.relayRows || []).slice()
        this.rebuildLadders()
        return
      }
      const name = this.kingdom.name || '新建王国'
      const concept = this.kingdom.mainConceptName || ''
      const subLabels = Array.isArray(this.kingdom.subConceptLabels) && this.kingdom.subConceptLabels.some(Boolean)
        ? this.kingdom.subConceptLabels
        : (Array.isArray(this.kingdom.subConceptNames) ? this.kingdom.subConceptNames : [])
      const subs = Array.isArray(this.kingdom.subConceptNames) ? this.kingdom.subConceptNames : []
      const attrs = [concept].concat(subs).filter(Boolean)
      this.model = {
        name,
        kind: this.kingdom.mainConceptLabel || concept || '新建王国',
        metrics: conceptMetrics(this.kingdom.mainConceptLabel || concept, subLabels),
        ladderTitle: '当前连板池',
        ladderSub: '按主概念从当日涨停池拆出',
        nonLeaderHeight: '—',
        mainAttrs: attrs.length ? attrs : ['未选主概念'],
        conceptChips: (attrs.length ? attrs : [name]).map((item) => [item, 1]),
        themeRows: [{
          name,
          since: '—',
          d5: '—',
          d10: '—',
          cum: '—',
          indep: '—',
          avgRed: '—',
          todayRed: '—',
          todayLimit: '—',
          ge4: '—',
          b3: '—',
          b2: '—',
          heat: 0
        }],
        catchupCols: [
          { key: 'ge4', title: '曾≥4B池', tone: 'rose', count: 0, total: '—', red: '—', rows: [] },
          { key: 'b3', title: '3B池', tone: 'orange', count: 0, total: '—', red: '—', rows: [] },
          { key: 'b2', title: '2B池', tone: 'green', count: 0, total: '—', red: '—', rows: [] }
        ]
      }
      this.heightRows = []
      this.relayRows = []
      this.rebuildLadders()
    },
    attrRaw(index) {
      const bar = this.heightBar || {}
      const raws = Array.isArray(bar.attrRaws) ? bar.attrRaws : bar.attrs
      return String((raws && raws[index]) || '').trim()
    },
    rebuildLadders() {
      if (!this.model) return
      const specs = this.ladderAttrSpecs
      const leader = this.leaderRole || {}
      const leaderCode = normCode6((leader.leader && (leader.leader.code || leader.leader.leaderId)) || leader.code)
      const mapped = leaderCode && this.conceptsByCode ? this.conceptsByCode[leaderCode] : null
      const leaderConcepts = Array.isArray(mapped) ? mapped : []
      const leaderName = (leader.leader && leader.leader.name) || leader.name || ''
      const buckets = { first: [], ge4: [], b3: [], b2: [] }
      if (specs.length) {
        (this.limitUpRows || []).forEach((row) => {
          if (!specs.some((spec) => rowMatchesConcept(row, spec.name, spec.source))) return
          const boards = boardCount(row)
          const line = ladderLine(row, leaderName, leaderConcepts, specs[0].name)
          if (boards <= 1) buckets.first.push(line)
          else if (boards >= 4) buckets.ge4.push(line)
          else if (boards === 3) buckets.b3.push(line)
          else if (boards === 2) buckets.b2.push(line)
        })
      }
      this.ladders = [
        { key: 'first', title: '首板池', heat: 'heat-1', rows: sortRows(buckets.first) },
        { key: 'b2', title: '2B池', heat: 'heat-2', rows: sortRows(buckets.b2) },
        { key: 'b3', title: '3B池', heat: 'heat-3', rows: sortRows(buckets.b3) },
        { key: 'ge4', title: '≥4B池', heat: 'heat-4', rows: sortRows(buckets.ge4) }
      ]
      const attrNames = specs.map((item) => item.name)
      const sub = !this.limitUpReady
        ? '正在读取当日涨停池…'
        : (attrNames.length
          ? `主属性「${attrNames.join(' / ')}」满足其一 · ${this.tradeDate || '当日'}涨停池`
          : '未选主属性1或主属性2，连板池为空')
      this.$set(this.model, 'ladderTitle', '当前连板池')
      this.$set(this.model, 'ladderSub', sub)
    },
    visibleLadderRows(col) {
      const rows = (col && col.rows) || []
      if (!col || col.key !== 'first' || this.ladderOpen) return rows
      return rows.slice(0, 4)
    },
    onHeightBar(payload) {
      this.heightBar = payload || null
    },
    canHeightPickRole(role) {
      return !!(
        this.heightBar &&
        this.heightBar.heightListPick &&
        role &&
        (role.key === 'trend' || role.key === 'board') &&
        role.code
      )
    },
    onRoleCardHeightPick(role) {
      if (!this.canHeightPickRole(role)) return
      const shell = this.$refs.themeShell
      const center = shell && shell.$refs && shell.$refs.center
      if (!center || typeof center.toggleHeightFromPool !== 'function') return
      center.toggleHeightFromPool({
        code: role.code,
        name: role.name,
        board: role.boardLabel || ''
      })
    },
    saveConceptAttrs() {
      if (!this.kingdom || !this.kingdom.id) {
        this.$message.warning('当前页没有可保存的王国')
        return
      }
      const raws = ((this.heightBar && this.heightBar.attrRaws) || []).slice(0, 3)
      while (raws.length < 3) raws.push('')
      this.$emit('save-main-attrs', {
        id: this.kingdom.id,
        mainAttrSlots: raws.map((item) => String(item || '').trim())
      })
    },
    clearConceptAttrs() {
      if (!this.kingdom || !this.kingdom.id) {
        this.$message.warning('当前页没有可清空的王国')
        return
      }
      const shell = this.$refs.themeShell
      if (shell && typeof shell.clearAllMainAttrs === 'function') shell.clearAllMainAttrs()
      this.$emit('save-main-attrs', {
        id: this.kingdom.id,
        mainAttrSlots: ['', '', ''],
        cleared: true
      })
    },
    onAttrAdd() {
      const shell = this.$refs.themeShell
      if (shell) shell.startMainAttrPick()
    },
    onAttrClear(index) {
      const shell = this.$refs.themeShell
      if (shell) shell.clearMainAttr(index)
    },
    visibleRoleSlots(role) {
      const slots = (role && role.slots) || []
      const filled = []
      for (let i = 0; i < slots.length; i += 1) {
        if (slots[i] && slots[i].code) filled.push({ slot: slots[i], i })
      }
      if (filled.length) return filled
      return [{ slot: slots[0] || {}, i: 0 }]
    },
    roleEmptyText(role) {
      if (!role || !role.code) return '尚未指定'
      return `${role.name || ''} ${role.code} 不在当前监控池`
    },
    kingdomStoreId() {
      return this.sectorTabKey
    },
    loadRoleOverrides() {
      this.roleOverrides = readRoleOverrides(this.kingdomStoreId())
    },
    saveRoleOverrides() {
      try {
        const keep = {}
        ;['trend', 'board'].forEach((key) => {
          const ov = this.roleOverrides && this.roleOverrides[key]
          if (ov) keep[key] = { slots: readStoredSlots(ov) }
        })
        localStorage.setItem(ROLE_OVERRIDE_PREFIX + this.kingdomStoreId(), JSON.stringify(keep))
      } catch (err) {
        console.warn('[kingdom] role override save failed', err)
      }
    },
    releaseClearedRoles() {
      const slots = this.roleSlots || []
      ;['trend', 'board'].forEach((key) => {
        const stored = readStoredSlots(this.roleOverrides && this.roleOverrides[key])
        if (!stored[0] || !stored[0].cleared) return
        const slot = slots.find((role) => role.key === key)
        const code = normCode6((slot && slot.code) || '')
        if (!code || code === '000000') return
        this.$emit('release-role', { sectorId: this.kingdomStoreId(), roleKey: key, code })
      })
    },
    resolveRoleSlot(raw, fallback, list) {
      let code = ''
      let name = ''
      let boardLabel = ''
      let manual = false
      let fromKingdom = false
      if (raw && raw.cleared) {
        manual = true
      } else if (raw && raw.code) {
        code = raw.code
        name = raw.name || ''
        boardLabel = raw.boardLabel || ''
        manual = true
      } else if (fallback && fallback.code) {
        code = fallback.code
        name = fallback.name || ''
        fromKingdom = true
      }
      const norm = normCode6(code)
      if (!norm || norm === '000000') {
        return { code: '', name: '', leader: null, rank: 1, manual, boardLabel: '', fromKingdom: false }
      }
      const rows = list || this.leaders || []
      const idx = rows.findIndex((row) => normCode6(row && (row.code || row.leaderId)) === norm)
      const leader = idx >= 0 ? rows[idx] : null
      let resolvedName = (leader && leader.name) || name || ''
      if (!resolvedName) {
        const lu = (this.limitUpRows || []).find((row) => normCode6(row && (row.code || row.ts_code)) === norm)
        if (lu && lu.name) resolvedName = String(lu.name)
      }
      return {
        code: norm,
        name: resolvedName,
        leader,
        rank: idx >= 0 ? idx + 1 : 1,
        manual,
        boardLabel,
        fromKingdom
      }
    },
    nextRoleSlotIndex(role) {
      if (role && role.key === 'leader') return 0
      const slots = (role && role.slots) || []
      const empty = slots.findIndex((slot) => !slot || !slot.code)
      if (empty >= 0) return empty
      return Math.max(slots.length - 1, 0)
    },
    quoteOf(code) {
      return (this.roleQuotes && this.roleQuotes[normCode6(code)]) || {}
    },
    pctText(value) {
      return formatSignedPct(value, 1)
    },
    pctTone(value) {
      const n = Number(value)
      if (!Number.isFinite(n) || n === 0) return 'flat'
      return n > 0 ? 'up' : 'down'
    },
    slotTags(slot) {
      const quote = this.quoteOf(slot && slot.code)
      const tags = []
      const board = String((slot && slot.boardLabel) || quote.boardLabel || '').trim()
      if (board && board !== '—') tags.push(board)
      if (quote.limitUpToday) tags.push('今日涨停')
      return tags
    },
    async loadRoleQuotes() {
      const codes = String(this.roleQuoteCodes || '').split(',').map((item) => item.trim()).filter(Boolean)
      const td = String(this.tradeDate || '').slice(0, 10)
      if (!td || !codes.length) {
        if (!codes.length) this.roleQuotes = {}
        return
      }
      const seq = ++this.roleQuoteSeq
      try {
        const res = await stockApi.postLeaderRoleReturns({ tradeDate: td, codes })
        if (seq !== this.roleQuoteSeq) return
        const map = {}
        const rows = (res && res.rows) || []
        for (let i = 0; i < rows.length; i += 1) {
          const code = normCode6(rows[i] && rows[i].code)
          if (code) map[code] = rows[i]
        }
        this.roleQuotes = map
      } catch (err) {
        if (seq !== this.roleQuoteSeq) return
        console.warn('[kingdom] role returns failed', err)
      }
    },
    toggleRolePick(key) {
      this.rolePick = this.rolePick === key ? '' : key
    },
    onRoleCardClick(role) {
      if (this.rolePick && role && this.rolePick !== role.key) {
        const slots = role.slots || []
        let index = 0
        for (let i = 0; i < slots.length; i += 1) {
          if (slots[i] && slots[i].code) {
            index = i
            break
          }
        }
        this.swapRoleSlots(this.rolePick, role.key, index)
        return
      }
      this.onRoleCardHeightPick(role)
    },
    onRoleSlotClick(role, index) {
      if (this.rolePick && role && this.rolePick !== role.key) {
        this.swapRoleSlots(this.rolePick, role.key, index)
        return
      }
      this.onRoleCardHeightPick(role)
    },
    slotPayload(key, index) {
      const card = (this.roleCards || []).find((role) => role.key === key)
      const slot = card && card.slots && card.slots[index]
      const code = normCode6(slot && slot.code)
      if (!code || code === '000000') return null
      return {
        code,
        name: (slot.leader && slot.leader.name) || slot.name || '',
        boardLabel: slot.boardLabel || ''
      }
    },
    swapSlotIndex(key) {
      const card = (this.roleCards || []).find((role) => role.key === key)
      const slots = (card && card.slots) || []
      if (key === 'leader') return 0
      const filled = []
      for (let i = 0; i < slots.length; i += 1) {
        if (slots[i] && slots[i].code) filled.push(i)
      }
      if (filled.length === 1) return filled[0]
      if (filled.length > 1) return this.nextRoleSlotIndex(card)
      return 0
    },
    writeOverrideSlot(key, index, payload) {
      if ((key !== 'trend' && key !== 'board') || index < 0) return
      const stored = readStoredSlots(this.roleOverrides && this.roleOverrides[key])
      const card = (this.roleCards || []).find((role) => role.key === key)
      const slots = (card && card.slots) || []
      if (!stored[0] && slots[0] && slots[0].code && index !== 0) {
        stored[0] = {
          code: normCode6(slots[0].code),
          name: slots[0].name || '',
          boardLabel: slots[0].boardLabel || ''
        }
      }
      if (!stored[1] && slots[1] && slots[1].code && index !== 1) {
        stored[1] = {
          code: normCode6(slots[1].code),
          name: slots[1].name || '',
          boardLabel: slots[1].boardLabel || ''
        }
      }
      if (payload && payload.code) {
        stored[index] = {
          code: normCode6(payload.code),
          name: payload.name || '',
          boardLabel: payload.boardLabel || ''
        }
      } else {
        stored[index] = index === 0 ? { cleared: true } : null
      }
      this.$set(this.roleOverrides, key, { slots: stored })
    },
    swapRoleSlots(pickKey, otherKey, otherIndex) {
      const pickIndex = this.swapSlotIndex(pickKey)
      const left = this.slotPayload(pickKey, pickIndex)
      const right = this.slotPayload(otherKey, otherIndex)
      if (!left && !right) return
      if (left && right && left.code === right.code) {
        this.$message.info('两边已是同一只股票')
        return
      }
      if (pickKey === 'leader' || otherKey === 'leader') {
        const leaderPayload = pickKey === 'leader' ? right : left
        const otherPayload = pickKey === 'leader' ? left : right
        const nonLeaderKey = pickKey === 'leader' ? otherKey : pickKey
        const nonLeaderIndex = pickKey === 'leader' ? otherIndex : pickIndex
        if (!leaderPayload) {
          this.$message.warning('总龙位置不能空着互换')
          return
        }
        if (!this.replaceLeader(leaderPayload, { keepPick: true, silent: true })) return
        this.writeOverrideSlot(nonLeaderKey, nonLeaderIndex, otherPayload)
        this.saveRoleOverrides()
      } else {
        this.writeOverrideSlot(pickKey, pickIndex, right)
        this.writeOverrideSlot(otherKey, otherIndex, left)
        this.saveRoleOverrides()
      }
      const leftName = (left && left.name) || '空位'
      const rightName = (right && right.name) || '空位'
      this.$message.success(`已互换：${leftName} ↔ ${rightName}`)
    },
    clearRoleSlot(key, index) {
      if ((key !== 'trend' && key !== 'board') || index < 0) return
      const card = (this.roleCards || []).find((role) => role.key === key)
      const slot = card && card.slots && card.slots[index]
      const code = normCode6(slot && slot.code)
      const stored = readStoredSlots(this.roleOverrides && this.roleOverrides[key])
        .filter((item) => normCode6(item && item.code) !== code)
      this.$set(this.roleOverrides, key, { slots: stored })
      this.saveRoleOverrides()
      if (this.kingdom && this.kingdom.id && code && code !== '000000') {
        const members = (this.kingdom.members || []).filter((item) => !(normCode6(item && item.code) === code && item.role === key))
        const leaderCode = (this.kingdom.roles && this.kingdom.roles.leader) || ''
        this.$emit('update-kingdom', {
          id: this.kingdom.id,
          roles: packRoleFields(leaderCode, members),
          members
        })
      }
    },
    assignRoleStock(key, payload) {
      if (key === 'leader') {
        this.replaceLeader(payload)
        return
      }
      if (key !== 'trend' && key !== 'board') return
      const code = normCode6(payload && payload.code)
      const name = String((payload && payload.name) || '').trim()
      if (!code || code === '000000') return
      const card = (this.roleCards || []).find((role) => role.key === key)
      const slots = (card && card.slots) || []
      const same = slots.findIndex((slot) => normCode6(slot && slot.code) === code)
      if (same >= 0) {
        this.$message.warning('这只股票已经在这张卡上')
        return
      }
      const stored = readStoredSlots(this.roleOverrides && this.roleOverrides[key])
      stored.push({
        code,
        name,
        boardLabel: String((payload && payload.boardLabel) || '').trim()
      })
      this.$set(this.roleOverrides, key, { slots: stored })
      this.saveRoleOverrides()
      if (this.kingdom && this.kingdom.id) {
        const members = ((this.kingdom.members) || []).slice()
        const hit = members.findIndex((item) => normCode6(item && item.code) === code)
        if (hit >= 0) members.splice(hit, 1, { ...members[hit], name: name || members[hit].name || '', role: key })
        else members.push({ code, name, role: key })
        const leaderCode = (this.kingdom.roles && this.kingdom.roles.leader) || ''
        this.$emit('update-kingdom', {
          id: this.kingdom.id,
          roles: packRoleFields(leaderCode, members),
          members
        })
      }
      this.rolePick = ''
      const label = key === 'board' ? '连板龙' : '趋势龙'
      this.$message.success(`已填入${label}：${name || code}`)
    },
    replaceLeader(payload, options) {
      const keepPick = !!(options && options.keepPick)
      const silent = !!(options && options.silent)
      const code = normCode6(payload && payload.code)
      const name = String((payload && payload.name) || '').trim()
      if (!code || code === '000000') return false
      if (!this.kingdom || !this.kingdom.id) {
        this.$message.warning('当前页没有可更换的王国总龙')
        return false
      }
      const roles = { leader: '', trend: '', board: '', ...(this.kingdom.roles || {}) }
      if (normCode6(roles.leader) === code) {
        if (!keepPick) this.rolePick = ''
        if (!silent) this.$message.info(`${name || code} 已经是总龙`)
        return false
      }
      const roleKeys = ['trend', 'board']
      for (let i = 0; i < roleKeys.length; i += 1) {
        const key = roleKeys[i]
        if (normCode6(roles[key]) === code) roles[key] = ''
        const stored = readStoredSlots(this.roleOverrides && this.roleOverrides[key])
        let changed = false
        const next = stored.map((slot) => {
          if (slot && normCode6(slot.code) === code) {
            changed = true
            return null
          }
          return slot
        })
        if (changed) this.$set(this.roleOverrides, key, { slots: next })
      }
      roles.leader = code
      const members = []
      const seen = new Set()
      const pushMember = (memberCode, memberName, role) => {
        const key = normCode6(memberCode)
        if (!key || key === '000000' || !role || seen.has(key)) return
        seen.add(key)
        members.push({ code: key, name: memberName || '', role })
      }
      pushMember(code, name, 'leader')
      const memberSrc = this.kingdom.members || []
      for (let i = 0; i < memberSrc.length; i += 1) {
        const item = memberSrc[i]
        const itemCode = normCode6(item && item.code)
        if (!itemCode || itemCode === code) continue
        const role = item && item.role
        if (role !== 'trend' && role !== 'board') continue
        pushMember(itemCode, item.name, role)
      }
      this.saveRoleOverrides()
      this.$emit('update-kingdom', { id: this.kingdom.id, roles: packRoleFields(code, members), members })
      if (!keepPick) this.rolePick = ''
      if (!silent) this.$message.success(`总龙已更换为 ${name || code}`)
      return true
    },
    onLadderPick(row) {
      if (!this.rolePick || !row) return
      this.assignRoleStock(this.rolePick, {
        code: row.code,
        name: row.name,
        boardLabel: row.boards ? `${row.boards}B` : ''
      })
    },
    onRoleStock(payload) {
      if (!this.rolePick) return
      this.assignRoleStock(this.rolePick, payload)
    },
    toneOf(text) {
      const raw = String(text || '')
      if (raw === '—' || raw === '+0.00%' || raw === '0%' || raw === '0.00%') return 'flat'
      if (raw.startsWith('-')) return 'down'
      if (raw.startsWith('+') || /%$/.test(raw)) return 'up'
      return ''
    },
    toast(message) {
      this.$message.info(message)
    },
    removeHeight(idx) {
      const row = this.heightRows[idx]
      this.heightRows.splice(idx, 1)
      this.$message.success(`已从本页名单移除 ${row ? row.name : ''}`)
    },
    removeRelay(idx) {
      const row = this.relayRows[idx]
      this.relayRows.splice(idx, 1)
      this.$message.success(`已从本页接力池移除 ${row ? row.name : ''}`)
    }
  }
}
</script>

<style lang="scss" scoped>
.kd {
  display: flex;
  flex-direction: column;
  gap: 10px;
  color: #1a2b3c;
  font-size: 12px;
}
.card {
  background: #fff;
  border: 1px solid #d7e6f8;
  border-radius: 12px;
  padding: 12px 14px;
}
.overview {
  display: flex;
  align-items: center;
  gap: 16px;
}
.ov-left { display: flex; align-items: center; gap: 10px; min-width: 240px; }
.crown {
  width: 36px; height: 36px; border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  background: #fff6df; color: #e0a100; font-size: 20px;
}
.ov-title { display: flex; align-items: center; gap: 8px; }
.ov-title b { font-size: 18px; }
.ov-king { margin-top: 2px; color: #7b8da6; }
.pill, .ghost, .primary, .pane-h button, .attr {
  border: 1px solid #d5e4f7;
  background: #f7faff;
  color: #2f5f9e;
  border-radius: 8px;
  cursor: pointer;
}
.pill { padding: 2px 8px; font-size: 12px; }
.ov-metrics { display: flex; gap: 18px; align-items: center; }
.ov-save, .ov-clear { align-self: center; height: 32px; white-space: nowrap; }
.ov-metric { display: flex; flex-direction: column; gap: 2px; min-width: 0; max-width: 180px; }
.ov-metric span, .ov-metric em { color: #8aa0c2; font-style: normal; font-size: 11px; }
.ov-metric b { font-size: 15px; line-height: 1.25; font-weight: 650; word-break: break-all; }
.ov-actions { display: flex; gap: 8px; align-items: flex-start; }
.important-enter { position: relative; }
.war-stack { display: flex; flex-direction: column; gap: 6px; }
.leave-important { min-height: 36px; white-space: nowrap; }
.important-menu {
  position: absolute;
  z-index: 8;
  top: calc(100% + 6px);
  left: 0;
  min-width: 148px;
  padding: 6px;
  border-radius: 10px;
  background: linear-gradient(180deg, #fffaf0, #f6e7c4);
  border: 1px solid #e4c56a;
  box-shadow: 0 10px 24px rgba(160, 110, 30, 0.18);
}
.important-menu button {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin: 0;
  padding: 8px;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: #6a4a16;
  font-weight: 700;
  cursor: pointer;
}
.important-menu button + button { margin-top: 2px; }
.important-menu button:hover { background: rgba(224, 160, 40, 0.18); }
.important-menu em {
  font-style: normal;
  font-size: 11px;
  color: #a07830;
}
.ov-height {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.ov-h-metric {
  display: flex;
  flex-direction: column;
  line-height: 1.15;
  padding-right: 4px;
}
.ov-h-metric span { color: #8aa0c2; font-size: 11px; }
.ov-h-metric b { color: #1a73e8; font-size: 22px; }
.ov-attr {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 4px 2px 8px;
  border: 1px solid #d5e2f4;
  border-radius: 4px;
  background: #fff;
}
.ov-attr.filled { border-color: #7eb0f0; background: #f7fbff; }
.ov-attr.next { border-color: #1a73e8; }
.ov-attr span { color: #5b6b82; font-size: 12px; font-weight: 700; }
.ov-attr b { color: #1a73e8; font-size: 13px; }
.ov-attr button {
  border: 1px solid #1a73e8;
  background: #1a73e8;
  color: #fff;
  border-radius: 4px;
  padding: 1px 8px;
  cursor: pointer;
  font-size: 12px;
}
.ov-attr button.x {
  border: 0;
  background: transparent;
  color: #8aa0c2;
  padding: 0 4px;
  font-size: 14px;
}
.ghost, .primary, .pane-h button { padding: 5px 10px; }
.primary { background: #1a73e8; color: #fff; border-color: #1a73e8; }
.up { color: #e02424; }
.down { color: #1f9d55; }
.flat { color: #5b6b82; }
.blue { color: #1a73e8; }
.roles, .ladder, .catchup { display: grid; gap: 10px; }
.roles {
  grid-template-columns: minmax(0, 2.2fr) minmax(240px, 1.25fr) minmax(240px, 1.25fr);
  align-items: start;
}
.role-shell {
  min-width: 0;
  border-radius: 14px;
  border: 1px solid #e4edf8;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 10px 24px rgba(42, 78, 130, 0.05);
}
.role-shell.tone-gold {
  background: linear-gradient(180deg, #fff6e4 0%, #fffdf8 64px, #fff 128px);
  border-color: #f0ddb0;
}
.role-shell.tone-blue {
  background: linear-gradient(180deg, #f2f7ff 0%, #f9fbff 64px, #fff 128px);
  border-color: #d4e4f8;
}
.role-shell.tone-rose {
  background: linear-gradient(180deg, #fff3f7 0%, #fffafb 64px, #fff 128px);
  border-color: #f4d4e1;
}
.role-shell.picking {
  box-shadow: 0 0 0 2px rgba(26, 115, 232, 0.35);
}
.role-shell.swap-target {
  cursor: pointer;
}
.role-shell.height-pick {
  cursor: pointer;
  box-shadow: 0 0 0 2px rgba(26, 115, 232, 0.55);
}
.role-head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 8px 0 14px;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.12em;
}
.role-pick {
  border: 1px solid #d5e4f7;
  background: #f7faff;
  color: #2f5f9e;
  border-radius: 6px;
  padding: 0 8px;
  height: 22px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0;
  cursor: pointer;
}
.role-pick.on {
  background: #1a73e8;
  color: #fff;
  border-color: #1a73e8;
}
.role-x {
  margin-left: auto;
  width: 16px;
  height: 16px;
  border: 0;
  background: transparent;
  color: #9aa8bd;
  font-size: 12px;
  line-height: 16px;
  padding: 0;
  cursor: pointer;
}
.role-x:hover { color: #e02424; }
.role-slots {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 10px 12px;
}
.role-slot {
  position: relative;
  min-width: 0;
  border-radius: 10px;
}
.role-slot.empty {
  border: 1px dashed #d5e2f4;
  background: rgba(255, 255, 255, 0.72);
  min-height: 72px;
}
.role-slot.next {
  box-shadow: 0 0 0 2px rgba(26, 115, 232, 0.45);
}
.slot-x {
  position: absolute;
  top: 4px;
  right: 6px;
  z-index: 2;
  width: 18px;
  height: 18px;
  border: 0;
  background: transparent;
  color: #9aa8bd;
  font-size: 14px;
  line-height: 18px;
  padding: 0;
  cursor: pointer;
}
.slot-x:hover { color: #e02424; }
.role-compact {
  padding: 8px 22px 4px 4px;
}
.compact-top {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 8px;
}
.compact-top b {
  font-size: 16px;
  line-height: 1.2;
}
.today {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  line-height: 1.15;
}
.today span { color: #90a4c0; font-size: 10px; }
.today em { font-style: normal; font-size: 16px; font-weight: 750; }
.compact-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 6px;
}
.compact-tags span {
  font-size: 11px;
  font-weight: 700;
  color: #c62828;
  background: #ffe8e8;
  border-radius: 6px;
  padding: 1px 6px;
}
.role-compact .returns {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px;
  margin-top: 8px;
}
.role-compact .ret {
  background: #f7f9fc;
  border-radius: 8px;
  padding: 6px 4px;
  text-align: center;
}
.role-compact .ret span { display: block; font-size: 10px; color: #90a4c0; }
.role-compact .ret b { display: block; margin-top: 2px; font-size: 13px; font-weight: 700; }
.role-pick-hint {
  margin: -4px 0 0;
  padding: 6px 10px;
  border-radius: 8px;
  background: #eef5ff;
  color: #1a5fbf;
  font-size: 12px;
}
.ladder table.role-picking tbody tr.pickable {
  cursor: pointer;
}
.ladder table.role-picking tbody tr.pickable:hover {
  background: #eaf2ff;
}
.tone-gold .role-head { color: #8d6414; }
.tone-blue .role-head { color: #1a5fbf; }
.tone-rose .role-head { color: #b83a66; }
.role-shell :deep(.leader-col) {
  min-width: 0;
  width: 100%;
}
.role-shell :deep(.leader-card) {
  border: 0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
}
.role-missing {
  padding: 16px 14px 18px;
  color: #8aa0c2;
}
.ladder { grid-template-columns: repeat(4, minmax(0, 1fr)); }
.catchup { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.role { display: flex; flex-direction: column; gap: 6px; }
.role-top, .role-name, .role-px, .mini-h, .pane-h, .block-h, .attr-row, .subtabs {
  display: flex; align-items: center; gap: 8px;
}
.role-top { justify-content: space-between; }
.role-tag {
  color: #fff; border-radius: 6px; padding: 2px 8px; font-weight: 700;
}
.tone-gold .role-tag { background: #e0a100; }
.tone-blue .role-tag { background: #1a73e8; }
.tone-rose .role-tag { background: #e85a8c; }
.role-badge {
  color: #fff; background: #e02424; border-radius: 10px; padding: 2px 8px; font-size: 12px;
}
.role-name b { font-size: 16px; }
.role-name span, .role-px em { color: #8aa0c2; font-style: normal; }
.role-px b { font-size: 18px; }
.role p { margin: 0; color: #5b6b82; min-height: 32px; }
.role-stats { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 6px; }
.role-stats div {
  background: #f6f9fd; border-radius: 8px; padding: 6px 4px; text-align: center;
}
.role-stats b { display: block; font-size: 13px; }
.role-stats span { color: #8aa0c2; font-size: 11px; }
.block-h { margin-bottom: 8px; }
.block-h .mark {
  width: 8px; height: 16px; border-radius: 3px; background: #1a73e8; display: inline-block;
}
.block-h b { font-size: 14px; }
.block-h span { color: #8aa0c2; margin-left: 8px; }
.block-h .primary { margin-left: auto; }
.mini { border: 1px solid #e3eefb; border-radius: 10px; overflow: hidden; background: #fff; }
.ladder table { width: 100%; table-layout: fixed; }
.ladder th, .ladder td {
  white-space: normal;
  word-break: break-all;
  padding: 4px 3px;
  font-size: 12px;
}
.fold {
  display: block;
  width: 100%;
  border: 0;
  border-top: 1px solid #e3eefb;
  background: #f7faff;
  color: #1a73e8;
  cursor: pointer;
  padding: 6px 8px;
  text-align: left;
}
.mini-h { padding: 6px 8px; color: #fff; }
.mini-h b { font-size: 13px; }
.mini-h em { margin-left: auto; font-style: normal; }
.tone-rose > .mini-h { background: #e85a8c; }
.tone-blue > .mini-h { background: #3b82f6; }
.tone-orange > .mini-h { background: #f5a623; }
.tone-green > .mini-h { background: #2faf6a; }
.ladder .mini.heat-1 { background: #f4f7fb; }
.ladder .mini.heat-1 > .mini-h {
  background: linear-gradient(90deg, #e7eef8, #c5d4ea);
  color: #2c4466;
}
.ladder .mini.heat-2 { background: #f3faf6; }
.ladder .mini.heat-2 > .mini-h {
  background: linear-gradient(90deg, #d7efe4, #7dcfab);
  color: #145c40;
}
.ladder .mini.heat-3 { background: #fff8ee; }
.ladder .mini.heat-3 > .mini-h {
  background: linear-gradient(90deg, #ffe0a8, #f39a2e);
  color: #6a3200;
}
.ladder .mini.heat-4 { background: #fff4f0; }
.ladder .mini.heat-4 > .mini-h {
  background: linear-gradient(90deg, #ffb089 0%, #ff7a45 22%, #e10600 78%, #d10000 100%);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.45);
  color: #fff;
}
.ladder .mini.heat-1 th { background: #f4f7fb; }
.ladder .mini.heat-2 th { background: #f3faf6; }
.ladder .mini.heat-3 th { background: #fff8ee; }
.ladder .mini.heat-4 th { background: #fff4f0; }
.mini-h.stats { gap: 10px; flex-wrap: wrap; }
.mini-h.stats strong { color: #fff; }
.mini-h.stats .up { color: #fff; }
table { width: 100%; border-collapse: collapse; }
th, td { padding: 4px 6px; text-align: left; white-space: nowrap; }
th { color: #7d90aa; font-weight: 600; background: #f8fbfe; }
td { border-top: 1px solid #f0f4fa; }
.nm { font-weight: 700; }
.split { display: grid; grid-template-columns: 1.15fr 0.85fr; gap: 10px; }
.pane { border: 1px solid #e3eefb; border-radius: 10px; overflow: auto; }
.pane-h { justify-content: space-between; padding: 6px 8px; background: #f7faff; }
.attr-row { margin: 4px 0 8px; flex-wrap: wrap; }
.height-pill, .attr {
  background: #f4f8fd; border-radius: 8px; padding: 4px 8px;
}
.height-pill span, .attr em { display: block; color: #8aa0c2; font-style: normal; font-size: 11px; }
.height-pill b { color: #1a73e8; font-size: 16px; }
.tag {
  display: inline-block; margin-right: 4px; padding: 0 6px; border-radius: 8px; font-size: 11px;
}
.tag.c0 { background: #e8f2ff; color: #1a73e8; }
.tag.c1 { background: #fff1e6; color: #d46b08; }
.tag.c2 { background: #f3e8ff; color: #722ed1; }
.tag.c3 { background: #e8f8ef; color: #1f9d55; }
.x {
  border: 0; background: transparent; color: #9aabc2; cursor: pointer; font-size: 14px;
}
.chips { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 8px; }
.chip {
  border: 1px solid #d5e4f7; background: #fff; border-radius: 14px; padding: 3px 8px; cursor: pointer;
}
.chip em {
  margin-left: 4px; font-style: normal; color: #1a73e8; font-weight: 700;
}
.subtabs { margin-bottom: 8px; }
.subtabs button {
  border: 1px solid #d5e4f7; background: #f7faff; color: #35598c;
  border-radius: 8px 8px 0 0; padding: 6px 12px; cursor: pointer;
}
.subtabs button.on { background: #1a73e8; color: #fff; border-color: #1a73e8; }
.table-wrap { overflow: auto; }
.theme th, .theme td { font-size: 12px; }
.stars { color: #f5a623; letter-spacing: 1px; }
.placeholder {
  background: #f7faff; border: 1px dashed #c5d8f0; border-radius: 8px; padding: 18px;
}
.placeholder p { margin: 6px 0 0; color: #5b6b82; }
.empty { text-align: center; color: #8aa0c2; }
.mask {
  position: fixed; inset: 0; background: rgba(20, 40, 70, 0.28);
  display: flex; align-items: center; justify-content: center; z-index: 30;
}
.dialog {
  width: 420px; background: #fff; border-radius: 12px; padding: 16px;
  border: 1px solid #d7e6f8;
}
.dialog.wide { width: 640px; }
.dialog header { font-size: 16px; font-weight: 800; margin-bottom: 8px; }
.dialog p { margin: 4px 0; }
.rename-input {
  width: 100%; box-sizing: border-box; margin: 8px 0 10px;
  border: 1px solid #d5e4f7; border-radius: 8px; padding: 8px 10px; font-size: 14px;
}
.rename-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 12px; }
.note { color: #8aa0c2; }
@media (max-width: 1280px) {
  .roles, .catchup { grid-template-columns: 1fr; }
  .ladder { grid-template-columns: 1fr 1fr; }
  .split, .overview { display: block; }
  .ov-metrics { margin: 8px 0; flex-wrap: wrap; }
  .ov-actions { margin-top: 8px; flex-wrap: wrap; }
  .ghost, .leave-important { min-height: 36px; }
}
</style>
